import { EllipsisVertical } from "lucide-react";
import {
  type ComponentProps,
  Fragment,
  type MouseEvent,
  type ReactNode,
} from "react";

import { cn } from "@krea/shared/utils";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./Dropdown";

export interface IActionItem {
  content: ReactNode;
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
  addSeparator?: boolean;
}

interface IActionMenuProps extends ComponentProps<typeof DropdownMenu> {
  label?: ReactNode;
  trigger?: ReactNode;
  /**
   * Accessible name for the trigger button. Required whenever `trigger` is
   * icon-only — the default ellipsis is, so a menu without this reads as an
   * unnamed button to assistive tech. It cannot arrive through `...rest`:
   * those props go to Radix's `Root`, which renders no DOM node at all.
   */
  triggerLabel?: string;
  /** Extends the trigger rather than replacing it — the base keeps the
   *  pointer cursor. Use it to give an icon-only trigger a real hit area. */
  triggerClassName?: string;
  /**
   * Make `trigger` BE the trigger element instead of its child. Opt-in, because
   * it is only correct when the trigger is itself a focusable control: pass a
   * Button and without this you get a button nested in Radix's own, pass a bare
   * icon and WITH this the menu has no named button at all.
   */
  triggerAsChild?: boolean;
  items: IActionItem[];
}

export const ActionMenu = ({
  items,
  label,
  trigger,
  triggerLabel,
  triggerClassName,
  triggerAsChild = false,
  ...rest
}: IActionMenuProps) => {
  if (items.length === 0) return null;

  return (
    <DropdownMenu modal={false} {...rest}>
      <DropdownMenuTrigger
        asChild={triggerAsChild}
        aria-label={triggerLabel}
        className={cn("cursor-pointer", triggerClassName)}
      >
        {trigger ?? <EllipsisVertical size={20} className="text-primary-300" />}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="z-500000">
        {label && <DropdownMenuLabel>{label}</DropdownMenuLabel>}
        {label && <DropdownMenuSeparator />}
        {/* Every call site passes a literal array built in place, so the menu's
            items never reorder, and `content` is a ReactNode with no id to key
            on — the index is the only stable identity available here. */}
        {items.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: see the note above.
          <Fragment key={index}>
            <DropdownMenuItem className="cursor-pointer" onClick={item.onClick}>
              {item.content}
            </DropdownMenuItem>
            {item.addSeparator && <DropdownMenuSeparator />}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
